/**
 * Admin — Chat commands
 *
 * The GLOBAL command catalog and the platform policy over it. This is the
 * policy half of the two-surface split: not "does this command answer in my
 * channel" (that's the per-channel Commands page) but "does it exist at all,
 * what does it answer by default, may a channel switch it off, and which of its
 * fields may a channel change".
 *
 * Super-admin only, routed at /admin/commands.
 *
 * Three API facts shape the page:
 *
 *   - THE CATALOG IS A UNION. A command may be known through a stored
 *     definition row or through a live registration in the bot's in-memory
 *     registry. A code-defined command with no row is PERMISSIVE — registered,
 *     unlocked, channel-disableable — and is shown here anyway, because it is
 *     governable before any row exists. The row is written on first save.
 *   - HANDLER-BACKED COMMANDS HAVE NO RESPONSE TEXT. Their output is computed
 *     in code; the API answers 400 rather than storing a value nothing reads.
 *     The field is shown disabled with that reason rather than hidden, so the
 *     difference between "locked" and "there is nothing to lock" is visible.
 *   - EVERY MUTATING ROUTE RETURNS THE FULL REFRESHED ENTRY, so each write
 *     replaces its row from the response. No optimistic state, no refetch.
 *
 * Deleting an admin-authored global CASCADES to every channel's override rows,
 * and the confirmation says so with the count. Leaving them behind would not be
 * harmless: with the definition gone, a surviving row carrying response text
 * reads as that channel's OWN command, so the delete would quietly turn itself
 * into a per-channel command in every channel that had reworded it.
 *
 * VARIABLE HELP (R20 chat 3, C5). Both response fields carry <VariableHelp>,
 * fed once from GET /api/admin/variables — the channel-free listing, because a
 * global command answers in every channel and no one channel's status is true
 * of it. Not fatal: a failed load toasts and the help offers a retry.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  adminCommandsApi,
  OVERRIDE_KEYS,
  OVERRIDE_KEY_LABELS,
  PERMISSION_LABELS,
  PERMISSION_LEVELS,
  type CommandEntry,
  type CommandPatch,
  type OverrideKey,
} from '@/api/commandsApi'
import { apiErrorMessage } from '@/api/client'
import { capabilitiesApi, type VariableInfo } from '@/api/capabilitiesApi'
import { useToast } from '@/components/Toast'
import VariableHelp, { VariableHelpProvider } from '@/components/VariableHelp'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Lock,
  Plus,
  Power,
  Save,
  Terminal,
  Trash2,
} from 'lucide-react'
import clsx from 'clsx'

export default function AdminCommands() {
  const [entries, setEntries] = useState<CommandEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [variables, setVariables] = useState<VariableInfo[] | null>(null)
  const [variablesError, setVariablesError] = useState<string | null>(null)

  const { success, error } = useToast()

  const load = useCallback(async () => {
    try {
      setEntries(await adminCommandsApi.list())
      setLoadError(null)
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Could not load the command catalog.'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const loadVariables = useCallback(async () => {
    setVariablesError(null)
    try {
      setVariables(await capabilitiesApi.adminVariables())
    } catch (err) {
      const message = apiErrorMessage(err, 'Could not load the variable list.')
      setVariables(null)
      setVariablesError(message)
      error(message)
    }
  }, [error])

  useEffect(() => {
    loadVariables()
  }, [loadVariables])

  const variableHelp = useMemo(
    () => ({
      variables,
      loadError: variablesError,
      retry: loadVariables,
      scoped: false,
    }),
    [variables, variablesError, loadVariables],
  )

  const replaceEntry = useCallback((updated: CommandEntry) => {
    setEntries((prev) =>
      prev.map((e) => (e.name === updated.name ? updated : e)),
    )
  }, [])

  /** Every catalog write goes through here: busy flag, row swap, toast. */
  const mutate = useCallback(
    async (
      name: string,
      call: () => Promise<CommandEntry>,
      successMessage: string,
      failureMessage: string,
    ) => {
      setBusy(name)
      try {
        replaceEntry(await call())
        success(successMessage)
      } catch (err) {
        error(apiErrorMessage(err, failureMessage))
      } finally {
        setBusy(null)
      }
    },
    [replaceEntry, success, error],
  )

  const handleToggleEnabled = (entry: CommandEntry) => {
    const turningOff = entry.enabled
    if (turningOff && entry.channel_override_count > 0) {
      const where =
        entry.channel_override_count === 1
          ? '1 channel has customised it'
          : `${entry.channel_override_count} channels have customised it`
      if (
        !confirm(
          `Disable !${entry.name} platform-wide?\n\n${where}. It stops answering everywhere, including in channels that customised it. Their changes are kept, so re-enabling here restores them.`,
        )
      ) {
        return
      }
    }
    mutate(
      entry.name,
      () => adminCommandsApi.patch(entry.name, { enabled: !entry.enabled }),
      turningOff
        ? `!${entry.name} is disabled platform-wide.`
        : `!${entry.name} is enabled platform-wide.`,
      `Could not change the kill switch for !${entry.name}.`,
    )
  }

  const handleSave = (entry: CommandEntry, patch: CommandPatch) => {
    if (Object.keys(patch).length === 0) {
      success('Nothing to save \u2014 no changes.')
      return
    }
    mutate(
      entry.name,
      () => adminCommandsApi.patch(entry.name, patch),
      `Saved !${entry.name}.`,
      `Could not save !${entry.name}.`,
    )
  }

  const handleDelete = async (entry: CommandEntry) => {
    const cascade =
      entry.channel_override_count > 0
        ? `\n\n${entry.channel_override_count} per-channel customisation${
            entry.channel_override_count === 1 ? '' : 's'
          } will be removed with it.`
        : ''
    if (
      !confirm(`Delete !${entry.name} from the platform?${cascade}\n\nThis cannot be undone.`)
    ) {
      return
    }
    setBusy(entry.name)
    try {
      const result = await adminCommandsApi.remove(entry.name)
      setEntries((prev) => prev.filter((e) => e.name !== entry.name))
      success(
        result.channel_rows_removed > 0
          ? `Deleted !${entry.name} and ${result.channel_rows_removed} channel customisation${
              result.channel_rows_removed === 1 ? '' : 's'
            }.`
          : `Deleted !${entry.name}.`,
      )
    } catch (err) {
      // 400 here is the one that matters: a code-defined command is disabled,
      // never deleted, and the API says so in a sentence.
      error(apiErrorMessage(err, `Could not delete !${entry.name}.`))
    } finally {
      setBusy(null)
    }
  }

  const handleCreate = async (
    name: string,
    responseText: string,
    permission: string,
    cooldown: number,
  ) => {
    setBusy('__new__')
    try {
      const created = await adminCommandsApi.create({
        name,
        response_text: responseText,
        permission,
        cooldown_seconds: cooldown,
      })
      setEntries((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      )
      setAdding(false)
      setExpanded(created.name)
      success(`Created !${created.name}.`)
    } catch (err) {
      error(apiErrorMessage(err, 'Could not create that command.'))
    } finally {
      setBusy(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }

  return (
    <VariableHelpProvider value={variableHelp}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Terminal size={24} className="text-synth-magenta" />
            Chat commands
          </h1>
          <p className="text-text-secondary text-sm mt-1">
            Platform policy for every command: what it answers by default, whether
            a channel may switch it off, and which of its fields a channel may
            change. Turning a command on <em>for a channel</em> happens on that
            channel's own Commands page.
          </p>
        </div>

        {loadError && (
          <div className="bg-state-danger-bg border border-state-danger rounded-lg p-3 flex items-center gap-3">
            <AlertCircle className="text-state-danger shrink-0" size={18} />
            <span className="text-state-danger text-sm">{loadError}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <h3 className="label mb-0">Catalog</h3>
          {!adding && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setAdding(true)}
              disabled={!!loadError}
            >
              <Plus size={15} className="mr-1.5" />
              Add command
            </button>
          )}
        </div>

        {adding && (
          <NewGlobalForm
            busy={busy === '__new__'}
            onCancel={() => setAdding(false)}
            onCreate={handleCreate}
          />
        )}

        {entries.length === 0 && !loadError ? (
          <div className="card">
            <p className="text-text-muted text-sm py-6 text-center">
              No commands yet. Commands registered by modules appear here as soon
              as the bot has loaded them.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <CommandCard
                key={entry.name}
                entry={entry}
                busy={busy === entry.name}
                isExpanded={expanded === entry.name}
                onToggleExpand={() =>
                  setExpanded((p) => (p === entry.name ? null : entry.name))
                }
                onToggleEnabled={() => handleToggleEnabled(entry)}
                onSave={(patch) => handleSave(entry, patch)}
                onDelete={() => handleDelete(entry)}
              />
            ))}
          </div>
        )}

        <p className="text-text-muted text-xs leading-relaxed">
          A command registered by a module needs no row here to work &mdash; until
          one exists it runs on permissive defaults, which is what stops a module
          being locked out the moment it loads. Saving anything on such a command
          writes its row. Responses can use variables &mdash; open{' '}
          <em>Variables</em> under any response to see them all.
        </p>
      </div>
    </VariableHelpProvider>
  )
}

/* ------------------------------------------------------------------ */
/* Add an admin-authored global                                        */
/* ------------------------------------------------------------------ */

function NewGlobalForm({
  busy,
  onCancel,
  onCreate,
}: {
  busy: boolean
  onCancel: () => void
  onCreate: (
    name: string,
    responseText: string,
    permission: string,
    cooldown: number,
  ) => void
}) {
  const [name, setName] = useState('')
  const [responseText, setResponseText] = useState('')
  const [permission, setPermission] = useState<string>('everyone')
  const [cooldown, setCooldown] = useState('0')
  const responseRef = useRef<HTMLTextAreaElement>(null)

  const cooldownValue = Number.parseInt(cooldown, 10)
  const cooldownValid = Number.isFinite(cooldownValue) && cooldownValue >= 0
  const canSubmit = name.trim().length > 0 && cooldownValid

  return (
    <div className="card card-pad-sm space-y-3">
      <p className="help-text mt-0">
        A command defined here exists on every channel without a deploy. Its
        answer is stored text &mdash; commands whose output is computed come
        from code and appear in the catalog on their own.
      </p>

      <div className="flex items-center gap-2">
        <span className="text-text-muted">!</span>
        <input
          className="input w-auto font-mono text-sm py-1.5"
          placeholder="command name"
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          aria-label="New command name"
        />
      </div>

      <div>
        <label className="label" htmlFor="new-global-response">
          Default response
        </label>
        <textarea
          id="new-global-response"
          ref={responseRef}
          className="textarea min-h-[4.5rem]"
          value={responseText}
          disabled={busy}
          placeholder="@$(user) hello!"
          onChange={(e) => setResponseText(e.target.value)}
        />
        <VariableHelp
          text={responseText}
          textareaRef={responseRef}
          onInsert={setResponseText}
          disabled={busy}
        />
        <p className="help-text">
          May be left empty, which means the command answers nothing &mdash; a
          real setting, not a missing one.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="label" htmlFor="new-global-permission">
            Default permission
          </label>
          <select
            id="new-global-permission"
            className="select w-auto text-sm py-1.5"
            value={permission}
            disabled={busy}
            onChange={(e) => setPermission(e.target.value)}
          >
            {PERMISSION_LEVELS.map((level) => (
              <option key={level} value={level}>
                {PERMISSION_LABELS[level]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="new-global-cooldown">
            Default cooldown (seconds)
          </label>
          <input
            id="new-global-cooldown"
            className="input w-24 text-sm py-1.5"
            type="number"
            min={0}
            value={cooldown}
            disabled={busy}
            onChange={(e) => setCooldown(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary btn-sm disabled:opacity-50"
          disabled={busy || !canSubmit}
          onClick={() => onCreate(name, responseText, permission, cooldownValue)}
        >
          {busy ? (
            <Loader2 size={15} className="mr-1.5 animate-spin" />
          ) : (
            <Plus size={15} className="mr-1.5" />
          )}
          Create
        </button>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        {!cooldownValid && (
          <span className="error-text">Cooldown must be 0 or more.</span>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* One command                                                         */
/* ------------------------------------------------------------------ */

function CommandCard({
  entry,
  busy,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  onSave,
  onDelete,
}: {
  entry: CommandEntry
  busy: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onToggleEnabled: () => void
  onSave: (patch: CommandPatch) => void
  onDelete: () => void
}) {
  return (
    <div
      className={clsx('card card-pad-sm', !entry.enabled && 'border-state-danger/40')}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          className="mt-0.5 p-1 -m-1 rounded text-text-muted hover:text-text-primary shrink-0"
          aria-label={isExpanded ? `Collapse ${entry.name}` : `Expand ${entry.name}`}
        >
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="code-inline text-sm">!{entry.name}</span>
            {entry.display_name && (
              <span className="text-text-primary font-medium">
                {entry.display_name}
              </span>
            )}
            {!entry.enabled && <span className="badge-danger">Globally off</span>}
            {entry.handler_backed ? (
              <span
                className="badge-info"
                title="Output is computed in code, so there is no response text"
              >
                From code
              </span>
            ) : (
              <span className="badge-neutral">Stored text</span>
            )}
            {entry.owner_module && (
              <span className="badge-neutral">{entry.owner_module}</span>
            )}
            {!entry.has_row && (
              <span
                className="badge-neutral"
                title="No stored policy yet — running on permissive defaults. Saving writes the row."
              >
                Not yet governed
              </span>
            )}
            {!entry.channel_disableable && (
              <span className="badge-neutral gap-1.5">
                <Lock size={11} />
                Always on
              </span>
            )}
          </div>

          <p className="text-text-muted text-xs mt-1 truncate">
            {entry.handler_backed
              ? entry.description || 'Answers from code.'
              : entry.response_text || '(answers nothing)'}
          </p>

          <p className="text-text-muted text-xs mt-1">
            {PERMISSION_LABELS[entry.permission] ?? entry.permission}
            {entry.cooldown_seconds > 0
              ? ` \u00b7 ${entry.cooldown_seconds}s cooldown`
              : ' \u00b7 no cooldown'}
            {' \u00b7 '}
            <span className="text-text-secondary tabular-nums">
              {entry.channel_override_count}
            </span>{' '}
            {entry.channel_override_count === 1
              ? 'channel customisation'
              : 'channel customisations'}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {busy && (
            <Loader2 className="animate-spin text-text-secondary" size={18} />
          )}
          <button
            onClick={onToggleEnabled}
            disabled={busy}
            className={clsx(
              'btn btn-sm disabled:opacity-50',
              entry.enabled ? 'btn-secondary' : 'btn-danger',
            )}
            title={
              entry.enabled
                ? 'Disable this command platform-wide'
                : 'Enable this command platform-wide'
            }
          >
            <Power size={15} className="mr-1.5" />
            {entry.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pl-8">
          <div className="divider" />
          <PolicyEditor
            key={entry.name}
            entry={entry}
            busy={busy}
            onSave={onSave}
            onDelete={onDelete}
          />
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Defaults + policy                                                   */
/* ------------------------------------------------------------------ */

/**
 * The definition's defaults and the two locks over them.
 *
 * `channel_disableable` is a separate control from the overridable-key list on
 * purpose, and the copy says why: "may a channel switch this off" is authority,
 * while the three keys are data. Folding the two together is the bug the module
 * platform carried until R5, where an `enabled` key living inside a settings
 * dict was mistaken for the enabled column.
 */
function PolicyEditor({
  entry,
  busy,
  onSave,
  onDelete,
}: {
  entry: CommandEntry
  busy: boolean
  onSave: (patch: CommandPatch) => void
  onDelete: () => void
}) {
  const [displayName, setDisplayName] = useState(entry.display_name ?? '')
  const [description, setDescription] = useState(entry.description ?? '')
  const [responseText, setResponseText] = useState(entry.response_text ?? '')
  const [permission, setPermission] = useState(entry.permission)
  const [cooldown, setCooldown] = useState(String(entry.cooldown_seconds))
  const [disableable, setDisableable] = useState(entry.channel_disableable)
  const [keys, setKeys] = useState<OverrideKey[]>(entry.overridable_keys)
  const responseRef = useRef<HTMLTextAreaElement>(null)

  const coolValue = Number.parseInt(cooldown, 10)
  const coolValid = Number.isFinite(coolValue) && coolValue >= 0

  const toggleKey = (key: OverrideKey) =>
    setKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )

  /**
   * Only what changed. Built key by key: an untouched field must not appear in
   * the object at all, because JSON.stringify keeps null and drops undefined,
   * and the API treats a present null as "write NULL".
   */
  const buildPatch = (): CommandPatch => {
    const patch: CommandPatch = {}

    const nextDisplay = displayName.trim() ? displayName : null
    if (nextDisplay !== entry.display_name) patch.display_name = nextDisplay

    const nextDescription = description.trim() ? description : null
    if (nextDescription !== entry.description) patch.description = nextDescription

    // Handler-backed commands have no text, so the field is never sent for
    // them — the API would answer 400, correctly.
    if (!entry.handler_backed && responseText !== (entry.response_text ?? '')) {
      patch.response_text = responseText
    }

    if (permission !== entry.permission) patch.permission = permission
    if (coolValid && coolValue !== entry.cooldown_seconds) {
      patch.cooldown_seconds = coolValue
    }
    if (disableable !== entry.channel_disableable) {
      patch.channel_disableable = disableable
    }

    const sameKeys =
      keys.length === entry.overridable_keys.length &&
      keys.every((k) => entry.overridable_keys.includes(k))
    if (!sameKeys) patch.overridable_keys = keys

    return patch
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[12rem]">
          <label className="label" htmlFor={`display-${entry.name}`}>
            Display name
          </label>
          <input
            id={`display-${entry.name}`}
            className="input text-sm py-1.5"
            value={displayName}
            disabled={busy}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div className="flex-1 min-w-[12rem]">
          <label className="label" htmlFor={`description-${entry.name}`}>
            Description
          </label>
          <input
            id={`description-${entry.name}`}
            className="input text-sm py-1.5"
            value={description}
            disabled={busy}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="label" htmlFor={`response-${entry.name}`}>
          Default response
        </label>
        <textarea
          id={`response-${entry.name}`}
          ref={responseRef}
          className="textarea min-h-[4.5rem]"
          value={entry.handler_backed ? '' : responseText}
          disabled={busy || entry.handler_backed}
          onChange={(e) => setResponseText(e.target.value)}
          placeholder={entry.handler_backed ? 'Built in code' : undefined}
        />
        {!entry.handler_backed && (
          <VariableHelp
            text={responseText}
            textareaRef={responseRef}
            onInsert={setResponseText}
            disabled={busy}
          />
        )}
        <p className="help-text">
          {entry.handler_backed
            ? 'This command builds its answer in code, so there is no text to set. Its permission and cooldown are still yours to change.'
            : 'An empty response is a real setting — the command answers nothing.'}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="label" htmlFor={`permission-${entry.name}`}>
            Default permission
          </label>
          <select
            id={`permission-${entry.name}`}
            className="select w-auto text-sm py-1.5"
            value={permission}
            disabled={busy}
            onChange={(e) => setPermission(e.target.value)}
          >
            {PERMISSION_LEVELS.map((level) => (
              <option key={level} value={level}>
                {PERMISSION_LABELS[level]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor={`cooldown-${entry.name}`}>
            Default cooldown (seconds)
          </label>
          <input
            id={`cooldown-${entry.name}`}
            className="input w-24 text-sm py-1.5"
            type="number"
            min={0}
            value={cooldown}
            disabled={busy}
            onChange={(e) => setCooldown(e.target.value)}
          />
        </div>
      </div>

      {!coolValid && <p className="error-text">Cooldown must be 0 or more.</p>}

      <div className="divider" />

      <div>
        <p className="label mb-2">What a channel may do</p>

        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={disableable}
            disabled={busy}
            onChange={() => setDisableable(!disableable)}
            className="accent-synth-magenta"
          />
          A channel may switch this command off
        </label>
        <p className="help-text">
          Separate from the fields below on purpose: this is authority, those are
          data. A channel may disable a command it cannot reword, and the other
          way round.
        </p>

        <p className="label mt-4 mb-2">Fields a channel may change</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {OVERRIDE_KEYS.map((key) => {
            const noText = key === 'response_text' && entry.handler_backed
            return (
              <label
                key={key}
                className={clsx(
                  'flex items-center gap-2 text-sm text-text-secondary',
                  noText && 'opacity-60',
                )}
                title={
                  noText
                    ? 'This command has no response text, so nothing changes either way.'
                    : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={keys.includes(key)}
                  disabled={busy}
                  onChange={() => toggleKey(key)}
                  className="accent-synth-magenta"
                />
                <span>{OVERRIDE_KEY_LABELS[key]}</span>
                {noText && (
                  <span className="text-text-muted text-xs">(no text)</span>
                )}
              </label>
            )
          })}
        </div>
        <p className="help-text">
          Unticking a field a channel has already changed leaves their value
          stored but stops it being used, from the next line in chat. They can
          still clear it.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary btn-sm disabled:opacity-50"
          disabled={busy || !coolValid}
          onClick={() => onSave(buildPatch())}
        >
          <Save size={15} className="mr-1.5" />
          Save
        </button>

        {entry.handler_backed ? (
          <span className="text-text-muted text-xs">
            Defined in code &mdash; disable it above rather than deleting it.
          </span>
        ) : (
          <button
            className="btn btn-danger btn-sm disabled:opacity-50"
            disabled={busy}
            onClick={onDelete}
          >
            <Trash2 size={15} className="mr-1.5" />
            Delete
          </button>
        )}
      </div>
    </div>
  )
}
